/**
 * Rude survey launcher factory for Web SDK
 */

import { Api, DynamicUxClientState } from "@ms-ofb/floodgate-shared-typings";
import { Utils } from "@ms-ofb/officefloodgatecore";
import * as Configuration from "../Configuration/Configuration";
import * as Logging from "../Logging/Logging";
import * as DynamicUxClient from "../UI/DynamicUx/DynamicUxClient";
import UISurvey from "./../FloodgateCore/UISurvey/UISurvey";
import * as Window from "./../Window/Window";

const { isNOU } = Utils;

export default class RudeSurveyLauncherFactory implements Api.ISurveyLauncherFactory {
	public static readonly LAUNCHER_TYPE: string = Api.LAUNCHER_TYPE_DEFAULT;

	public makeSurveyLauncher(survey: Api.ISurvey): Api.ISurveyLauncher {
		if (this.AcceptsSurvey(survey)) {
			return new RudeSurveyLauncher(survey);
		}

		return null;
	}

	public AcceptsSurvey(survey: Api.ISurvey): boolean {
		// This factory should accept any type that is not of type GenericMessagingSurface
		// This will ensure that a rude survey launcher is returned for any survey type old or new
		// as a launcher is needed to invoke survey activated callback
		if (!isNOU(survey) &&
			survey.getType() !== Api.SurveyType.GenericMessagingSurface &&
			survey.getType() !== Api.SurveyType.Dialog) {
			// Check for dynamic UX state if the survey is using UxSchema,
			// whether the status is success/failure, or is loading and no result yet
			if (survey.getUxSchema()) {
				if (Configuration.get().getDynamicUxConfig().enabled) {
					const dynamicUxClient = DynamicUxClient.get();
					const dynamicUxState = dynamicUxClient.getStatus();

					// reject only if the survey has only UxSchema and DynamicUx is failed to download
					if (dynamicUxState === DynamicUxClientState.Failure && !survey.isContentValid()) {
						return false;
					}
					return true;
				}
				else {
					return survey.isContentValid();
				}
			}
			return true;
		}

		return false;
	}
}

export class RudeSurveyLauncher implements Api.ISurveyLauncher {
	private survey: Api.ISurvey;

	constructor(survey: Api.ISurvey) {
		this.survey = survey;
	}

	public launch(): void {
		try {
			Window.get().OfficeBrowserFeedback.floodgate.showSurvey(new UISurvey(this.survey)).catch((error) => {
				Logging.getLogger().logEvent(Logging.EventIds.SURVEY_LAUNCHER_SHOWSURVEY_FAILURE,
					Logging.LogLevel.Error,
					{
						CampaignId: this.survey.getCampaignId(),
						SurveyId: this.survey.getSurveyInfo().getId(),
						SurveyType: this.survey.getType(),
						ErrorMessage: error && error.toString(),
					}
				);
			});
		}
		catch (error) {
			Logging.getLogger().logEvent(Logging.EventIds.SURVEY_LAUNCHER_FAILURE,
				Logging.LogLevel.Error,
				{
					CampaignId: this.survey.getCampaignId(),
					SurveyId: this.survey.getSurveyInfo().getId(),
					SurveyType: this.survey.getType(),
					ErrorMessage: error && error.toString(),
				}
			);
		}
	}
}
